<template>
  <div>
    <v-card elevation="0" class="textLight--text rounded-xl" style="border: 1px solid #EBEBEB;">
      <ValidationObserver ref="observer">
        <v-row class="pa-5">
          <v-col cols="12" class="d-flex justify-end">
            <v-switch
              style="width: 40px"
              class="ma-0"
              flat
              dense
              hide-details
              inset
              color="success"
              v-model="trainingCategory.isActive"
            >
            </v-switch>
          </v-col>

          <v-col cols="12">
            <v-row align="center">
              <v-col
                v-if="$vuetify.breakpoint.smOnly || $vuetify.breakpoint.lgAndUp"
                sm="4"
                lg="4"
                class="text-start align-self-end mb-2"
              >
                <span class="font-weight-thin">
                  {{ $t("trainingCategory.trainingCategory") }}
                </span>
              </v-col>

              <v-col cols="12" sm="8" lg="8" @click="error = null">
                <LocalisedContentEditable
                  :values="name"
                  @update:values="values => (this.name = values)"
                  :editable="true"
                  :textAreaDense="true"
                  css-class="text-h4 font-weight-bold textLight--text"
                  :showLanguageTabs="true"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col v-if="trainingSubCategories" cols="12">
            <v-card elevation="0" style="border: 1px solid #EBEBEB;" class="px-3 py-4 rounded-lg">
              <v-row>
                <v-col cols="12" class="text-start align-self-end">
                  <span class="font-weight-regular">
                    {{ $t("trainingCategory.trainingSubCategories") }}
                  </span>

                  <v-divider />
                </v-col>
              </v-row>

              <v-row
                v-for="(
                  trainingSubCategory, trainingSubCategoryIndex
                ) in trainingSubCategories"
                :key="`trainingSubCategory_${trainingSubCategoryIndex}`"
                align="center"
              >
                <v-col
                  v-if="
                    $vuetify.breakpoint.smOnly || $vuetify.breakpoint.lgAndUp
                  "
                  sm="4"
                  lg="4"
                  class="text-start align-self-end mb-2"
                >
                  <span class="font-weight-thin">
                    {{ $t("trainingCategory.trainingSubCategory") }}
                  </span>
                </v-col>

                <v-col cols="12" sm lg @click="error = null">
                  <LocalisedContentEditable
                    :values.sync="trainingSubCategory.name"
                    :editable="true"
                    :textAreaDense="true"
                    css-class="text-h4 font-weight-bold textLight--text"
                    :showLanguageTabs="true"
                  />
                </v-col>

                <v-col
                  cols="12"
                  sm="auto"
                  lg="auto"
                  class="
                    align-self-end
                    d-flex d-sm-block d-lg-block
                    justify-end
                  "
                >
                  <v-switch
                    flat
                    dense
                    hide-details
                    inset
                    color="success"
                    v-model="trainingSubCategory.isActive"
                    class="ma-0 me-2 me-sm-0 me-lg-0"
                    @change="value => (trainingSubCategory.isActive = value)"
                    style="width: 40px; height: 36px"
                  >
                  </v-switch>

                  <v-icon
                    size="16"
                    color="error"
                    class="mb-1 mb-sm-0 mb-lg-0"
                    @click="
                      removeTrainingSubCategory({
                        trainingSubCategory,
                        trainingSubCategoryIndex,
                      })
                    "
                  >
                    mdi-trash-can-outline
                  </v-icon>
                </v-col>
              </v-row>

              <v-row align="center">
                <v-col cols="12">
                  <v-btn
                    v-if="!btnLoading"
                    depressed
                    min-height="45"
                    :disabled="btnDisable"
                    block
                    color="#F8F8F8"
                    style="text-transform: unset; letter-spacing: 0.5px;"
                    class="rounded-lg font-weight-medium primary--text"
                    @click="addTrainingSubCategory"
                  >
                    <v-icon small left>mdi-plus</v-icon>
                    {{ $t("trainingCategory.addTrainingSubCategory") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-row align="center">
              <v-col cols="12">
                <v-btn
                  :loading="btnLoading"
                  depressed
                  min-height="48"
                  :disabled="btnDisable"
                  block
                  color="primary"
                  style="text-transform: unset; letter-spacing: 0.5px;"
                  class="rounded-lg font-weight-medium elevation-2 mt-4"
                  @click="createOrUpdate"
                >
                  {{ isCreate ? $t("trainingCategory.add") : $t("update") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" v-if="error">
            <p class="pa-0 ma-0 error--text">
              {{ error }}
            </p>
          </v-col>
        </v-row>
      </ValidationObserver>
    </v-card>
  </div>
</template>

<script>
import LocalisedContentEditable from "@/app/core-module/components/LocalisedContentEditable";

import { createOrUpdateTrainingCategory } from "../methods/createOrUpdateTrainingCategory";
import "../../core-module/util/validationRules";

export default {
  name: "CreateOrUpdateCategory",
  components: {
    LocalisedContentEditable,
  },
  props: {
    trainingCategoryData: {
      type: Object,
      default: () => {},
    },
    isCreate: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      trainingCategory: {},
      trainingSubCategories: [],
      deletedTrainingSubCategories: [],
      name: null,
      btnDisable: true,
      btnLoading: false,
      error: null,
    };
  },
  watch: {
    trainingCategoryData: {
      immediate: true,
      handler() {
        this.trainingCategory =
          JSON.parse(JSON.stringify(this.trainingCategoryData)) || {};

        this.trainingSubCategories = this.trainingCategory.subCategories ?? [];
        this.deletedTrainingSubCategories = [];

        this.name = this.trainingCategoryData?.name?.length
          ? this.trainingCategoryData.name
          : [];

        this.trainingCategory.isActive = this.isCreate
          ? true
          : this.trainingCategoryData?.isActive;

        this.error = null;
        this.btnDisable = true;
      },
      deep: true,
    },
    name: {
      immediate: true,
      handler() {
        this.btnDisable = !this.isValid(this.name);
      },
    },
  },
  methods: {
    isValid(val) {
      return !!this.filterEmptyLocalisedValues(val).length;
    },
    addTrainingSubCategory() {
      this.trainingSubCategories.push({
        name: [],
        isActive: true,
      });
    },
    removeTrainingSubCategory({
      trainingSubCategory,
      trainingSubCategoryIndex,
    }) {
      this.$dialog
        .confirm(this.$t("trainingCategory.confirmDelete"))
        .then(() => {
          this.trainingSubCategories.splice(trainingSubCategoryIndex, 1);

          if (trainingSubCategory._id) {
            this.deletedTrainingSubCategories.push(trainingSubCategory._id);
          }
        })
        .catch(_ => {});
    },
    async createOrUpdate() {
      const isValidForm = await this.$refs.observer.validate();

      if (isValidForm) {
        this.btnLoading = true;

        this.trainingSubCategories = this.trainingSubCategories?.reduce(
          (result, item) => {
            item.name = this.filterEmptyLocalisedValues(item.name);

            if (item.name.length) {
              delete item.category;
              result.push(item);
            } else if (item._id) {
              this.deletedTrainingSubCategories?.push(item._id);
            }

            return result;
          },
          []
        );

        let data = {
          name: this.filterEmptyLocalisedValues(this.name),
          isActive: this.trainingCategory?.isActive,
          subCategories: this.trainingSubCategories,
          deletedSubCategories: this.deletedTrainingSubCategories,
        };

        if (this.trainingCategory?._id) data._id = this.trainingCategory._id;

        await createOrUpdateTrainingCategory(data).then(response => {
          if (response.data) {
            if (!this.trainingCategory._id) {
              this.$emit("onTrainingCategoryCreate", response?.data?._id);
            } else {
              this.$emit("close");
            }
          } else if (response.error) {
            this.error = this.$t("somethingWentWrong");
          }

          this.btnLoading = false;
        });
      }
    },
  },
};
</script>
